import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { AuthState, User, LoginCredentials, UserRole } from "../../types";
import { LoginUsingEmail } from "../../services/endpoints/auth.endpoint";
import { GetProfile } from "../../services/endpoints/profile.endpoint";
import { storageService } from "../../services/storage.service";
import type { RootState } from "../store";

const storedToken = storageService.getToken();
const storedRole = storageService.getRole() as UserRole | null;

const initialState: AuthState = {
  isAuthenticated: Boolean(storedToken),
  token: storedToken,
  user: null,
  isLoading: false,
  error: null,
  role: storedRole,
};

export const loginThunk = createAsyncThunk<
  { user: User; role: UserRole; token: string },
  LoginCredentials,
  { state: RootState; rejectValue: string }
>("auth/login", (credentials, { rejectWithValue }) => {
  return LoginUsingEmail(credentials)
    .then((loginRes) => {
      const token = loginRes.data.token;
      storageService.setToken(token);
      storageService.setRole(credentials.role);

      return GetProfile().then((profileRes) => {
        return {
          user: profileRes.data,
          role: credentials.role,
          token,
        };
      });
    })
    .catch((error: any) => rejectWithValue(error.message));
});

export const getProfileThunk = createAsyncThunk<
  User,
  void | { force?: boolean },
  { state: RootState; rejectValue: string }
>(
  "auth/getProfile",
  (_, { rejectWithValue }) => {
    return GetProfile()
      .then((res) => res.data)
      .catch((error: any) => rejectWithValue(error.message));
  },
  {
    condition: (param, { getState }) => {
      const { auth } = getState();
      const force = typeof param === "object" && param?.force;
      if (force) return true;
      return !auth.user && !auth.isLoading && !auth.error;
    },
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: (state) => {
      state.isAuthenticated = false;
      state.token = null;
      state.user = null;
      state.role = null;
      state.isLoading = false;
      state.error = null;

      storageService.clearAuth();
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.token = action.payload.token;
        state.user = action.payload.user;
        state.role = action.payload.role;
        state.error = null;
      })
      .addCase(loginThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || null;
      })
      .addCase(getProfileThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getProfileThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
        state.error = null;
      })
      .addCase(getProfileThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || null;
      });
  },
});

export const { logout } = authSlice.actions;
export default authSlice.reducer;
